import { Globe, Send, Settings, X } from 'lucide-react';
import { LiksTooltip } from '../LiksTooltip/LiksTooltip';
import { LiksChatComposerAttachmentsMenu } from './LiksChatComposerAttachmentsMenu';
import { LiksChatComposerImageMenu } from './LiksChatComposerImageMenu';
import { LiksChatComposerKnowledgeMenu } from './LiksChatComposerKnowledgeMenu';
import { LiksChatComposerSpeechButton } from './LiksChatComposerSpeechButton';
import type { LiksChatComposerProps } from './LiksChatComposer.types';
import { useLiksChatComposer } from './useLiksChatComposer';
import './LiksChatComposer.css';

export type {
  LiksChatComposerExclusiveAction,
  LiksChatComposerExclusiveLockLabels,
  LiksChatComposerKnowledgeBaseOption,
  LiksChatComposerProps,
  LiksChatComposerSpeechInput,
  LiksChatComposerStagedAttachment,
} from './LiksChatComposer.types';

export function LiksChatComposer(props: LiksChatComposerProps) {
  const {
    placeholder,
    attachmentsMenuLabel,
    attachmentsEmptyLabel,
    uploadAttachmentLabel,
    removeAttachmentLabel,
    uploadingAttachmentLabel,
    attachmentUploadFailedLabel,
    addLabel,
    sendLabel,
    clearLabel,
    imageGenerationMenuLabel,
    imageGenerationToggleLabel,
    imageFormatLabel,
    imageFormatLabels,
    imageAspectRatioLabel,
    imageAspectRatioLabels,
    knowledgeBaseSearchPlaceholder,
    knowledgeBaseSearchLabel,
    knowledgeBaseEmptyLabel,
    knowledgeBaseLoadingLabel,
    knowledgeBaseMenuLabel,
    searchClearLabel = clearLabel,
    isKnowledgeBaseMenuLoading = false,
    speechInput,
    ragSettingsLabel,
    onOpenRagSettings,
    className,
    disabled = false,
  } = props;

  const {
    message,
    setMessage,
    textareaRef,
    fileInputRef,
    attachmentsMenuRef,
    knowledgeMenuRef,
    imageMenuRef,
    isAttachmentsMenuOpen,
    setIsAttachmentsMenuOpen,
    isKnowledgeMenuOpen,
    setIsKnowledgeMenuOpen,
    isImageMenuOpen,
    setIsImageMenuOpen,
    knowledgeSearchQuery,
    setKnowledgeSearchQuery,
    webSearchEnabled,
    imageGenerationEnabled,
    imageFormat,
    setImageFormat,
    imageAspectRatio,
    setImageAspectRatio,
    imageFormatGroupName,
    imageAspectRatioGroupName,
    speechElapsedMs,
    isSpeechRecording,
    isSpeechRequesting,
    isSpeechTranscribing,
    canSend,
    hasText,
    isControlsDisabled,
    filteredKnowledgeBaseOptions,
    linkedKnowledgeBaseCount,
    stagedAttachments,
    isAttachmentsLocked,
    isKnowledgeLocked,
    isWebSearchLocked,
    isImageGenerationLocked,
    attachmentsTooltip,
    knowledgeTooltip,
    webSearchTooltip,
    imageGenerationTooltip,
    handleClear,
    handleAddFiles,
    handleRemoveStagedAttachment,
    handleSend,
    handleKnowledgeBaseToggle,
    handleWebSearchToggle,
    handleImageGenerationToggle,
    handleKeyDown,
    closeMenusAndToggleSpeech,
  } = useLiksChatComposer(props);

  return (
    <div
      className={['liks-chat-composer', className].filter(Boolean).join(' ')}
    >
      <div className="liks-chat-composer__input-row">
        <textarea
          ref={textareaRef}
          className="liks-chat-composer__input liks-scrollbar"
          rows={1}
          value={message}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isControlsDisabled}
        />
        {hasText && (
          <button
            type="button"
            className="liks-chat-composer__clear"
            aria-label={clearLabel}
            onClick={handleClear}
            disabled={isControlsDisabled}
          >
            <X
              className="liks-chat-composer__clear-icon"
              strokeWidth={2}
              aria-hidden
            />
          </button>
        )}
        {speechInput != null && (
          <LiksChatComposerSpeechButton
            speechInput={speechInput}
            isSpeechRecording={isSpeechRecording}
            isSpeechRequesting={isSpeechRequesting}
            isSpeechTranscribing={isSpeechTranscribing}
            speechElapsedMs={speechElapsedMs}
            disabled={disabled}
            onToggle={closeMenusAndToggleSpeech}
          />
        )}
        <button
          type="button"
          className="liks-chat-composer__send"
          aria-label={sendLabel}
          disabled={!canSend}
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleSend}
        >
          <Send
            className="liks-chat-composer__send-icon"
            strokeWidth={2}
            aria-hidden
          />
        </button>
      </div>

      <div className="liks-chat-composer__actions-row">
        <LiksChatComposerAttachmentsMenu
          menuRef={attachmentsMenuRef}
          fileInputRef={fileInputRef}
          tooltip={attachmentsTooltip}
          menuLabel={attachmentsMenuLabel}
          emptyLabel={attachmentsEmptyLabel}
          uploadLabel={uploadAttachmentLabel}
          removeLabel={removeAttachmentLabel}
          uploadingLabel={uploadingAttachmentLabel}
          uploadFailedLabel={attachmentUploadFailedLabel}
          stagedAttachments={stagedAttachments}
          isOpen={isAttachmentsMenuOpen}
          isLocked={isAttachmentsLocked}
          isControlsDisabled={isControlsDisabled}
          onToggleOpen={() => {
            if (isAttachmentsLocked) {
              return;
            }
            setIsKnowledgeMenuOpen(false);
            setIsImageMenuOpen(false);
            setIsAttachmentsMenuOpen((isOpen) => !isOpen);
          }}
          onAddFiles={handleAddFiles}
          onRemove={handleRemoveStagedAttachment}
        />

        <LiksChatComposerKnowledgeMenu
          menuRef={knowledgeMenuRef}
          tooltip={knowledgeTooltip}
          menuLabel={knowledgeBaseMenuLabel}
          searchPlaceholder={knowledgeBaseSearchPlaceholder}
          searchLabel={knowledgeBaseSearchLabel}
          searchClearLabel={searchClearLabel}
          emptyLabel={knowledgeBaseEmptyLabel}
          loadingLabel={knowledgeBaseLoadingLabel}
          addLabel={addLabel}
          options={filteredKnowledgeBaseOptions}
          linkedCount={linkedKnowledgeBaseCount}
          searchQuery={knowledgeSearchQuery}
          onSearchQueryChange={setKnowledgeSearchQuery}
          isOpen={isKnowledgeMenuOpen}
          isLocked={isKnowledgeLocked}
          isLoading={isKnowledgeBaseMenuLoading}
          isControlsDisabled={isControlsDisabled}
          onToggleOpen={() => {
            if (isKnowledgeLocked) {
              return;
            }
            setIsAttachmentsMenuOpen(false);
            setIsImageMenuOpen(false);
            setIsKnowledgeMenuOpen((isOpen) => !isOpen);
          }}
          onToggleKnowledgeBase={handleKnowledgeBaseToggle}
        />

        <LiksTooltip content={webSearchTooltip} placement="top-start">
          <button
            type="button"
            className={[
              'liks-chat-composer__attach',
              webSearchEnabled && 'liks-chat-composer__attach--active',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={webSearchTooltip}
            aria-pressed={webSearchEnabled}
            onClick={handleWebSearchToggle}
            disabled={isControlsDisabled || isWebSearchLocked}
          >
            <Globe
              className="liks-chat-composer__attach-icon"
              strokeWidth={2}
              aria-hidden
            />
          </button>
        </LiksTooltip>

        <LiksChatComposerImageMenu
          menuRef={imageMenuRef}
          tooltip={imageGenerationTooltip}
          menuLabel={imageGenerationMenuLabel}
          toggleLabel={imageGenerationToggleLabel}
          formatLabel={imageFormatLabel}
          formatLabels={imageFormatLabels}
          aspectRatioLabel={imageAspectRatioLabel}
          aspectRatioLabels={imageAspectRatioLabels}
          imageFormatGroupName={imageFormatGroupName}
          imageAspectRatioGroupName={imageAspectRatioGroupName}
          imageGenerationEnabled={imageGenerationEnabled}
          imageFormat={imageFormat}
          imageAspectRatio={imageAspectRatio}
          isOpen={isImageMenuOpen}
          isLocked={isImageGenerationLocked}
          isControlsDisabled={isControlsDisabled}
          onToggleOpen={() => {
            if (isImageGenerationLocked) {
              return;
            }
            setIsAttachmentsMenuOpen(false);
            setIsKnowledgeMenuOpen(false);
            setIsImageMenuOpen((isOpen) => !isOpen);
          }}
          onToggleEnabled={handleImageGenerationToggle}
          onFormatChange={setImageFormat}
          onAspectRatioChange={setImageAspectRatio}
        />

        {ragSettingsLabel != null && onOpenRagSettings != null && (
          <div className="liks-chat-composer__actions-spacer">
            <LiksTooltip content={ragSettingsLabel} placement="top-end">
              <button
                type="button"
                className="liks-chat-composer__attach"
                aria-label={ragSettingsLabel}
                disabled={isControlsDisabled}
                onClick={() => {
                  setIsAttachmentsMenuOpen(false);
                  setIsKnowledgeMenuOpen(false);
                  setIsImageMenuOpen(false);
                  onOpenRagSettings();
                }}
              >
                <Settings
                  className="liks-chat-composer__attach-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </button>
            </LiksTooltip>
          </div>
        )}
      </div>
    </div>
  );
}
